/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Page from "src/components/Page";
import {
  CircularProgress,
  Container,
  Grid,
  Typography,
  makeStyles,
  Card,
  CardContent,
  Link,
  colors,
} from "@material-ui/core";
import { appPages } from "../../../config/appPages";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBlogBaseURL, urlAPIConf } from "src/config/api";
import { connect } from "react-redux";
import safeLogger from "src/services/safeLogger";
import { getBaseLanguage } from "../reports/utils/helpers";

const useStyles = makeStyles((theme) => ({
  spinner: {
    position: "absolute",
    top: "48%",
    left: "48%",
  },
  root: {
    paddingTop: "16px",
  },
  pageTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 300,
    color: colors.grey[800],
  },
  sectionTitle: {
    marginBottom: "20px",
    marginTop: "32px",
  },
}));

const useCardStyles = makeStyles((theme) => ({
  card: {
    position: "relative",
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[5],
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    minHeight: "60px",
    paddingLeft: "8px",
  },
  content: {
    flex: "1 0 auto",
    paddingRight: theme.spacing(6),
  },
  titleLink: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    textDecoration: "none",
  },
  arrowButton: {
    position: "absolute",
    right: theme.spacing(2),
    top: "50%",
    paddingRight: "10px",
    transform: "translateY(-50%)",
    fontSize: "1.5rem",
    color: theme.palette.grey[500],
    transition: "color 0.2s ease",
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
}));

// const getObjectLink = (objectType, id, parentId) => {
//   if (objectType === "projects") {
//     return `/projects/${id}`;
//   } else if (objectType === "samples") {
//     return `/reports/${id}`;
//   }
// };

const SearchCard = ({ result }) => {
  const classes = useCardStyles();
  const link = result.slug
    ? result.slug
    : `/${urlAPIConf.ARTICLES_SLUG}/${result.id}`;

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <Typography>
          <Link
            component={RouterLink}
            to={link}
            className={classes.titleLink}
            color="primary"
          >
            {result.title}
          </Link>
        </Typography>
        <Typography variant="body2">
          {result.description || result.intro}
        </Typography>
      </CardContent>

      <Link to={link} component={RouterLink} className={classes.arrowButton}>
        ›
      </Link>
    </Card>
  );
};

const SearchSection = (props) => {
  const { title, data } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <div>
      <Typography component="h4" variant="h2" className={classes.sectionTitle}>
        {t(title)}
      </Typography>
      {data.map((result, index) => (
        <SearchCard key={index} result={result} />
      ))}
    </div>
  );
};

const mapStateToProps = (state) => {
  return { userObject: state.userObject, appConf: state.appConf };
};

const ConnectedSearchResultsPage = (props) => {
  const classes = useStyles();
  const { userObject, appConf } = props;
  const userSearchConf = userObject?.settings?.general?.["searchConf"] || [
    "pages",
    "tutorials",
    "announcements",
  ];
  const [apiLoading, setApiLoading] = useState(true);
  const [offlineSearchLoading, setOfflineSearchLoading] = useState(true);
  const loading = apiLoading || offlineSearchLoading;
  const [searchResult, setSearchResult] = useState({
    Pages: [],
    Projects: [],
    Samples: [],
    Variants: [],
    Settings: [],
  });
  const query = useLocation().search;
  const { t } = useTranslation();

  // const searchCallback = (data, status) => {
  //   if (status === 200) {
  //     safeLogger(data);
  //   } else {
  //     safeLogger(data);
  //   }

  //   setApiLoading(false);
  // };

  // useEffect(() => {
  //   apiSearch(query, searchCallback);
  // }, []);

  useEffect(() => {
    let mounted = true;
    const q = new URLSearchParams(query).get("q");
    const category = new URLSearchParams(query).get("category");
    const tags = new URLSearchParams(query).get("tags");
    const author = new URLSearchParams(query).get("author");

    if ((q || category || tags || author) && appConf) {
      let filteredPages = [];
      if (q && userSearchConf.includes("pages")) {
        const queryRegex = new RegExp(q.split("+").join("|"), "i");

        filteredPages = appPages.filter((page) => {
          return (
            queryRegex.test(page.title) || queryRegex.test(page.description)
          );
        });
      }

      // const blogCategories = {
      //   tutorials: 1,
      //   announcements: 2,
      //   analysis: 3,
      // }; // TODO based on settings, include the category in the query

      setOfflineSearchLoading(false);
      setApiLoading(true);

      fetch(
        `${getBlogBaseURL()}/${
          urlAPIConf.BASE_API_SLUG
        }/pages/?fields=_,id,title,intro,first_published_at,categories,tags,main_image&type=blog.BlogPage&locale=${getBaseLanguage()}&order=-first_published_at${
          q ? `&search=${q}&search_operator=and` : ""
        }${category ? `&categories__in=${category}` : ""}${
          tags ? `&tags=${tags}` : ""
        }${author ? `&owner=${author}` : ""}`
      )
        .then((response) => response.json())
        .then((data) => {
          const blogResult = {};

          if (data.items) {
            data.items.forEach((item) => {
              const categoryName = item.categories?.[0]?.name || "Others";
              if (!blogResult[categoryName]) {
                blogResult[categoryName] = [];
              }
              blogResult[categoryName].push(item);
            });
          }

          if (mounted) {
            setSearchResult((prev) => ({
              ...prev,
              Pages: filteredPages,
              ...blogResult,
            }));
          }

          setApiLoading(false);
        });
    }

    return () => {
      mounted = false;
    };
  }, [query, appConf]);

  useEffect(() => {
    if (!loading) {
      safeLogger(searchResult);
    }
  }, [loading]);

  if (loading) {
    return <CircularProgress className={classes.spinner} />;
  } else {
    return (
      <Page className={classes.root} title={t("Search Results")}>
        <Container>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                component="h2"
                variant="h2"
                className={classes.pageTitle}
              >
                {t("Search Results")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {!loading &&
                Object.keys(searchResult).map((sectionKey, index) => {
                  if (searchResult[sectionKey].length) {
                    return (
                      <SearchSection
                        key={index}
                        title={sectionKey}
                        data={searchResult[sectionKey]}
                      />
                    );
                  } else return null;
                })}
            </Grid>
          </Grid>
        </Container>
      </Page>
    );
  }
};

export const SearchResultsPage = connect(mapStateToProps)(
  ConnectedSearchResultsPage
);
